import styles from "../../styles/addReceipt.module.css";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { showRestanteError } from "./Alerts";
import apiFetch from "../../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../../shared/utils/formatCurrency";

export function ReceiptFooter({
  selectedFilial,
  setSelectedFilial,
  setFacturasData,
  facturasData,
  receiptData,
  setReceiptData,
  setDisplayValueReceipt,
  restante,
  onCreated,
  validate,
  clearErrors,
  errors = {},
  montoEsValido,
  reloadCxc, // fuerza recarga de CxC tras guardar
  soloSaldoFavor,      // true cuando monto=0 y solo se aplica saldo a favor
  referenciaAutoSaldo, // referencia automática del recibo de origen
  cuerpoAcumular,      // useSaldoFavor: destino y rubro del sobrante
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const submittingRef = useRef(false);

  // El recibo y las facturas que se le aplican van en UNA sola petición: el
  // backend los guarda en una transacción. Antes se mandaba el recibo primero y
  // después una petición por factura; si la conexión se cortaba a mitad quedaba el
  // recibo creado, sin aplicar y sin movimiento bancario.
  async function crearRecibo() {
    // Si es solo aplicación de saldo a favor (monto=0),
    // usar la referencia automática del recibo origen como documento.
    const documentoFinal = soloSaldoFavor
      ? (referenciaAutoSaldo || "Aplicación saldo a favor")
      : receiptData.documento;
    const detalles = facturasData
      .filter((f) => f.checked && f.abono !== 0)
      .map((f) => ({
        factura: f.factura.id_factura,
        abono: f.abono,
        tratar_como_diferencial: f.tratar_como_diferencial ?? false,
        comision_bancaria: f.comision_bancaria ?? 0,
      }));
    const res = await apiFetch("/api/finance/recibo/", {
      method: "POST",
      body: JSON.stringify({
        ...receiptData,
        filial: selectedFilial,
        documento: documentoFinal,
        detalles,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(mensajeDeError(err));
    }
    return res.json();
  }

  // El backend devuelve los errores de las facturas anidados bajo `detalles`;
  // sin desenrollarlos el usuario solo veía "Error al crear recibo".
  function mensajeDeError(err) {
    if (!err || typeof err !== "object") return "Error al crear recibo";
    if (err.detail) return err.detail;
    if (err.detalles) {
      const textos = [];
      const recorrer = (v) => {
        if (typeof v === "string") textos.push(v);
        else if (Array.isArray(v)) v.forEach(recorrer);
        else if (v && typeof v === "object") Object.values(v).forEach(recorrer);
      };
      recorrer(err.detalles);
      if (textos.length) return textos.join(" · ");
    }
    const primera = Object.values(err)[0];
    if (Array.isArray(primera) && typeof primera[0] === "string") return primera[0];
    return "Error al crear recibo";
  }

  async function handleSubmit() {
    setApiError("");

    // Validación 1: campos de formulario
    // Pasar montoEsValido a validate() para que sepa cuándo monto=0 es aceptable
    if (validate) {
      const valid = validate(montoEsValido, soloSaldoFavor);
      if (!valid) return;
    }

    // Validación 2: el restante debe ser 0 para guardar (tolerancia de 0.05 por redondeo)
    if (Math.abs(restante) > 0.05) {
      showRestanteError().fire();
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      // Limpiar formulario y resetear estado del botón

      const aplicadas = facturasData.filter((f) => f.checked && f.abono !== 0);
      const recibo = await crearRecibo();

      if (aplicadas.length === 0) {
        // Sin facturas: redirigir a aplicar
        navigate(`/finanzas/recibos/${recibo.id}/aplicar`);
        return;
      }

      if (recibo.advertencia_asiento) {
        setApiError(recibo.advertencia_asiento);
      }
      handleReset();

      if (clearErrors) clearErrors();
      setSubmitting(false); // ← desbloquear botón tras éxito

      // Recargar CxC para que estén listas para el próximo recibo
      if (reloadCxc) reloadCxc();

      // Notificar al padre (AddReceipt) con el recibo creado para ofrecer el correo
      if (onCreated) {
        onCreated(recibo);
      } else {
        navigate("/finanzas/recibos");
      }
    } catch (err) {
      console.error("Error creando recibo:", err);
      setApiError(err.message);
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  // ── Guardar dejando el sobrante como saldo a favor ────────────────────
  // Lo que no se aplique a ninguna factura queda como crédito de la filial, y
  // queda YA: movimiento bancario, asiento DEBE Banco / HABER Cuentas Pagadas por
  // Adelantado y la CxC en negativo. El recibo no pasa por la cola de pendientes;
  // se guarda resuelto.
  //
  // Cubre los cuatro casos que antes no tenían salida, porque guardar exigía
  // restante = 0 y ninguno de ellos lo podía llevar a 0: la filial sin ninguna
  // factura, la que solo tiene una ya en saldo a favor, la que tiene pendientes
  // que no se quieren tocar, y el mixto (pagar unas y dejar el resto a favor).
  async function guardarComoSaldoAFavor() {
    setApiError("");

    // Mismos campos obligatorios que el guardado normal, exigiendo monto > 0: acá
    // entra plata de verdad al banco, así que el atajo de monto 0 (aplicar solo un
    // saldo a favor que ya existe) no corresponde.
    if (validate) {
      const valid = validate(() => Number(receiptData.monto) > 0, false);
      if (!valid) return;
    }
    // Sin sobrante no hay nada que acumular, y el backend rechazaría la llamada
    // DESPUÉS de haber creado el recibo y sus facturas. Se avisa antes.
    if (Math.abs(restante) <= 0.05) {
      setApiError(
        "No queda sobrante: las facturas marcadas consumen todo el recibo. "
        + "Usá Guardar Recibo.",
      );
      return;
    }
    let cuerpo;
    try {
      cuerpo = cuerpoAcumular(receiptData.fecha_pago);
    } catch (err) {
      setApiError(err.message);
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      // Las facturas marcadas, si las hay, viajan en el mismo POST del recibo; el
      // sobrante lo manda `acumular_saldo`, que deja un solo asiento cuadrado con
      // el HABER de cada factura pagada más el del crédito.
      const recibo = await crearRecibo();
      const res = await apiFetch(
        `/api/finance/recibo/${recibo.id}/acumular_saldo/`,
        { method: "POST", body: JSON.stringify(cuerpo) },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.detail
            ?? (Object.keys(data).length
                  ? JSON.stringify(data)
                  : `Error ${res.status} al guardar el saldo a favor`),
        );
      }
      if (data.advertencia_asiento) setApiError(data.advertencia_asiento);

      // El texto se arma acá, antes de limpiar el formulario: después de
      // handleReset() la moneda del recibo ya no está para elegir el símbolo.
      const simbolo = (receiptData.moneda_pago || "CRC") === "USD" ? "$" : "₡";
      const textoFavor = data.monto
        ? `Quedaron ${simbolo}${formatCurrency(Number(data.monto))} como `
          + `saldo a favor en ${data.factura}.`
        : "";

      handleReset();
      if (clearErrors) clearErrors();
      setSubmitting(false);
      if (reloadCxc) reloadCxc();
      if (onCreated) onCreated(recibo, textoFavor);
      else navigate("/finanzas/recibos");
    } catch (err) {
      console.error("Error guardando el saldo a favor:", err);
      setApiError(err.message);
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function handleReset() {
    setFacturasData((prev) =>
      prev.map((inv) => ({ ...inv, abono: 0, favor: 0, checked: false })),
    );
    setSelectedFilial(0);
    setReceiptData({
      monto: 0,
      cuenta_bancaria: 0,
      metodo_pago: "TRANSFERENCIA",
      documento: "",
      descripcion: "",
      fecha_pago: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0],
      moneda_pago: "CRC",
      tipo_cambio: 1,
    });
    setDisplayValueReceipt("");
    submittingRef.current = false;
  }

  const errorCount = Object.keys(errors).length;

  return (
    <div className={styles["receipt-footer"]}>
      {/* Resumen de errores de validación */}
      {errorCount > 0 && (
        <div
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.35)",
            borderLeft: "4px solid #ef4444",
            borderRadius: 8,
            padding: "12px 18px",
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#f87171",
              fontWeight: 600,
              marginBottom: errorCount > 1 ? 6 : 0,
            }}
          >
            <i className="fa-solid fa-triangle-exclamation" />
            {errorCount === 1
              ? Object.values(errors)[0]
              : `Hay ${errorCount} campos con error:`}
          </div>
          {errorCount > 1 && (
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                color: "#fca5a5",
                fontSize: "0.83rem",
              }}
            >
              {Object.values(errors).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Error de API */}
      {apiError && (
        <div
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.35)",
            borderLeft: "4px solid #ef4444",
            borderRadius: 8,
            padding: "10px 16px",
            marginBottom: 12,
            color: "#f87171",
            fontSize: "0.87rem",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <i className="fa-solid fa-circle-exclamation" />
          {apiError}
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={`${styles.btn} ${styles["btn-clean-form"]}`}
          onClick={handleReset}
          disabled={submitting}
        >
          <i className="fa-solid fa-repeat" /> Limpiar Formulario
        </button>
        <button
          type="button"
          onClick={guardarComoSaldoAFavor}
          disabled={submitting}
          title="Lo que no se aplique a ninguna factura queda como crédito de la filial, con su movimiento bancario y su asiento"
          style={{
            padding: "9px 18px",
            background: "rgba(167,139,250,0.15)",
            border: "1px solid rgba(167,139,250,0.45)",
            borderRadius: 6,
            color: "#a78bfa",
            fontWeight: 600,
            fontSize: 14,
            cursor: submitting ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 7,
            opacity: submitting ? 0.7 : 1,
            transition: "all 0.15s",
          }}
        >
          <i className="fa-solid fa-piggy-bank" /> Guardar como saldo a favor
        </button>
        <button
          className={`${styles.btn} ${styles["btn-save"]}`}
          onClick={handleSubmit}
          disabled={submitting}
          style={{ opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              &nbsp;Guardando…
            </>
          ) : (
            <>
              <i className="fa-solid fa-save" /> Guardar Recibo
            </>
          )}
        </button>
      </div>
    </div>
  );
}
